import {
  formatCptySettlementType,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  NumberInput,
  Tooltip,
  useCallbackRef,
  type ColumnDef,
  type NumericField,
} from '@talos/kyoko';
import type { ColDef, GridApi, ICellRendererParams, IRowNode, ValueGetterParams } from 'ag-grid-community';
import { useMemo, type KeyboardEvent } from 'react';
import type { ManualFillCareOrderBlotterRow } from './CareOrderManualFillsBlotter';
import {
  focusNextManualFillInput,
  manualFillAmountInputID,
  ManualFillInputColumn,
  manualFillPriceInputID,
  manualFillQtyInputID,
  type ManualFillInputColumn as ManualFillInputColumnType,
} from './manualFillInputFocus';

interface UseCareOrderManualFillsBlotterColumnsProps {
  onEntryFillQtyChange: (careOrderID: string, value: string | undefined, legSymbol?: string) => void;
  onEntryFillPriceChange: (careOrderID: string, value: string | undefined, legSymbol?: string) => void;
  onEntryFillPriceReset: (careOrderID: string, legSymbol?: string) => void;
  onEntryFillAmountChange: (careOrderID: string, value: string | undefined, legSymbol?: string) => void;
  onEntryFillAmountReset: (careOrderID: string, legSymbol?: string) => void;
  onEntryRemove: (careOrderID: string) => void;
  isLoading: boolean;
}

interface FillQtyInputCellRendererParams {
  onEntryFillQtyChange: UseCareOrderManualFillsBlotterColumnsProps['onEntryFillQtyChange'];
}

interface FillPriceInputCellRendererParams {
  onEntryFillPriceChange: UseCareOrderManualFillsBlotterColumnsProps['onEntryFillPriceChange'];
  onEntryFillPriceReset: UseCareOrderManualFillsBlotterColumnsProps['onEntryFillPriceReset'];
}

interface FillAmountInputCellRendererParams {
  onEntryFillAmountChange: UseCareOrderManualFillsBlotterColumnsProps['onEntryFillAmountChange'];
  onEntryFillAmountReset: UseCareOrderManualFillsBlotterColumnsProps['onEntryFillAmountReset'];
}

interface RemoveEntryCellRendererParams {
  onEntryRemove: UseCareOrderManualFillsBlotterColumnsProps['onEntryRemove'];
  isLoading: boolean;
}

export const useCareOrderManualFillsBlotterColumns = ({
  onEntryFillQtyChange,
  onEntryFillPriceChange,
  onEntryFillPriceReset,
  onEntryFillAmountChange,
  onEntryFillAmountReset,
  onEntryRemove,
  isLoading,
}: UseCareOrderManualFillsBlotterColumnsProps) => {
  return useMemo(
    () =>
      [
        {
          type: 'text',
          field: 'CptySettlementType',
          title: 'Type',
          suppressKeyboardEvent: () => true,
          initialWidth: 60,
          params: {
            getLabel: value => formatCptySettlementType(value),
          },
        },
        {
          type: 'security',
          field: 'Symbol',
          title: 'Symbol',
          suppressKeyboardEvent: () => true,
          initialWidth: 150,
        },
        {
          type: 'counterparty',
          field: 'Counterparty',
          title: 'Counterparty',
          suppressKeyboardEvent: () => true,
          initialWidth: 180,
        },
        {
          type: 'text',
          field: 'initiatingFrom',
          title: 'Initiating From',
          suppressKeyboardEvent: () => true,
          initialWidth: 120,
        },
        {
          type: 'size',
          field: 'CumQty',
          title: 'Filled Qty',
          suppressKeyboardEvent: () => true,
          initialWidth: 120,
          params: {
            currencyField: 'Currency',
          },
        },
        {
          type: 'size',
          field: 'LeavesQty',
          title: 'Remaining Qty',
          suppressKeyboardEvent: () => true,
          initialWidth: 120,
          params: {
            currencyField: 'Currency',
          },
        },
        {
          type: 'custom',
          id: 'fillQty',
          title: 'Fill Qty',
          headerClass: 'ag-right-aligned-cell',
          initialFlex: 1,
          minWidth: 150,
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ManualFillCareOrderBlotterRow>) => params.data,
            cellRendererParams: { onEntryFillQtyChange },
            cellRenderer: FillQtyInputCellRenderer,
          } satisfies ColDef<ManualFillCareOrderBlotterRow>,
        },
        {
          type: 'custom',
          id: 'fillPrice',
          title: 'Fill Price',
          headerClass: 'ag-right-aligned-cell',
          initialFlex: 1,
          minWidth: 150,
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ManualFillCareOrderBlotterRow>) => params.data,
            cellRendererParams: { onEntryFillPriceChange, onEntryFillPriceReset },
            cellRenderer: FillPriceInputCellRenderer,
          } satisfies ColDef<ManualFillCareOrderBlotterRow>,
        },
        {
          type: 'custom',
          id: 'fillAmount',
          title: 'Fill Amount',
          headerClass: 'ag-right-aligned-cell',
          initialFlex: 1,
          minWidth: 150,
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ManualFillCareOrderBlotterRow>) => params.data,
            cellRendererParams: { onEntryFillAmountChange, onEntryFillAmountReset },
            cellRenderer: FillAmountInputCellRenderer,
          } satisfies ColDef<ManualFillCareOrderBlotterRow>,
        },
        {
          type: 'custom',
          id: 'removeEntry',
          title: '',
          width: 36,
          minWidth: 36,
          maxWidth: 36,
          pinned: 'right',
          frozen: true,
          suppressColumnsToolPanel: true,
          exportable: false,
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ManualFillCareOrderBlotterRow>) => params.data,
            cellRendererParams: { onEntryRemove, isLoading },
            cellRenderer: RemoveEntryCellRenderer,
          } satisfies ColDef<ManualFillCareOrderBlotterRow>,
        },
      ] satisfies ColumnDef<ManualFillCareOrderBlotterRow>[],
    [
      isLoading,
      onEntryFillAmountChange,
      onEntryFillAmountReset,
      onEntryFillPriceChange,
      onEntryFillPriceReset,
      onEntryFillQtyChange,
      onEntryRemove,
    ]
  );
};

function FillQtyInputCellRenderer({
  value,
  api,
  node,
  onEntryFillQtyChange,
}: ICellRendererParams<ManualFillCareOrderBlotterRow, ManualFillCareOrderBlotterRow> & FillQtyInputCellRendererParams) {
  const handleChange = useCallbackRef((fillQty: string | undefined) => {
    if (!value) {
      return;
    }

    onEntryFillQtyChange(getManualFillRowCareOrderID(value), fillQty, getManualFillRowLegSymbol(value));
  });

  if (!value) {
    return <></>;
  }

  return (
    <ManualFillNumberInput
      field={value.fillQty}
      node={node}
      id={manualFillQtyInputID(value.rowID)}
      gridApi={api}
      column={ManualFillInputColumn.Qty}
      onChange={handleChange}
      suffix={getManualFillRowCurrency(value)}
      data-testid={`care-order-manual-fill-qty-input-${value.rowID}`}
    />
  );
}

function FillPriceInputCellRenderer({
  value,
  api,
  node,
  onEntryFillPriceChange,
  onEntryFillPriceReset,
}: ICellRendererParams<ManualFillCareOrderBlotterRow, ManualFillCareOrderBlotterRow> &
  FillPriceInputCellRendererParams) {
  const handleChange = useCallbackRef((fillPrice: string | undefined) => {
    if (!value) {
      return;
    }

    onEntryFillPriceChange(getManualFillRowCareOrderID(value), fillPrice, getManualFillRowLegSymbol(value));
  });
  const handleReset = useCallbackRef(() => {
    if (!value) {
      return;
    }

    onEntryFillPriceReset(getManualFillRowCareOrderID(value), getManualFillRowLegSymbol(value));
  });

  if (!value) {
    return <></>;
  }

  return (
    <ManualFillNumberInput
      field={value.fillPrice}
      node={node}
      id={manualFillPriceInputID(value.rowID)}
      gridApi={api}
      column={ManualFillInputColumn.Price}
      onChange={handleChange}
      onReset={canAutoPopulateFillPrice(value) ? handleReset : undefined}
      resetDisabled={value.isAutoPopulating.fillPrice}
      suffix={getManualFillRowAmountCurrency(value)}
      data-testid={`care-order-manual-fill-price-input-${value.rowID}`}
    />
  );
}

function FillAmountInputCellRenderer({
  value,
  api,
  node,
  onEntryFillAmountChange,
  onEntryFillAmountReset,
}: ICellRendererParams<ManualFillCareOrderBlotterRow, ManualFillCareOrderBlotterRow> &
  FillAmountInputCellRendererParams) {
  const handleChange = useCallbackRef((fillAmount: string | undefined) => {
    if (!value) {
      return;
    }

    onEntryFillAmountChange(getManualFillRowCareOrderID(value), fillAmount, getManualFillRowLegSymbol(value));
  });
  const handleReset = useCallbackRef(() => {
    if (!value) {
      return;
    }

    onEntryFillAmountReset(getManualFillRowCareOrderID(value), getManualFillRowLegSymbol(value));
  });

  if (!value) {
    return <></>;
  }

  return (
    <ManualFillNumberInput
      field={value.fillAmount}
      node={node}
      id={manualFillAmountInputID(value.rowID)}
      gridApi={api}
      column={ManualFillInputColumn.Amount}
      onChange={handleChange}
      onReset={handleReset}
      resetDisabled={value.isAutoPopulating.fillAmount}
      suffix={getManualFillRowAmountCurrency(value)}
      data-testid={`care-order-manual-fill-amount-input-${value.rowID}`}
    />
  );
}

function RemoveEntryCellRenderer({
  value,
  onEntryRemove,
  isLoading,
}: ICellRendererParams<ManualFillCareOrderBlotterRow, ManualFillCareOrderBlotterRow> & RemoveEntryCellRendererParams) {
  const handleClick = useCallbackRef(() => {
    if (!value) {
      return;
    }

    onEntryRemove(getManualFillRowCareOrderID(value));
  });

  if (!value || value.isLegEntry) {
    return <></>;
  }

  return (
    <IconButton
      icon={IconName.Close}
      size={FormControlSizes.Tiny}
      ghost
      disabled={isLoading}
      onClick={handleClick}
      data-testid={`care-order-manual-fill-remove-button-${value.rowID}`}
    />
  );
}

function getManualFillRowCareOrderID(row: ManualFillCareOrderBlotterRow) {
  return row.isLegEntry ? row.careOrderID : row.careOrder.OrderID;
}

function getManualFillRowLegSymbol(row: ManualFillCareOrderBlotterRow) {
  return row.isLegEntry ? row.legSymbol : undefined;
}

function getManualFillRowCurrency(row: ManualFillCareOrderBlotterRow | undefined) {
  if (!row) {
    return undefined;
  }

  return row.Currency;
}

function getManualFillRowAmountCurrency(row: ManualFillCareOrderBlotterRow | undefined) {
  if (!row) {
    return undefined;
  }

  return row.AmountCurrency;
}

function canAutoPopulateFillPrice(row: ManualFillCareOrderBlotterRow) {
  return !row.isLegEntry && row.legEntries != null && row.legEntries.length > 0;
}

function ManualFillNumberInput({
  field,
  node,
  gridApi,
  column,
  onReset,
  resetDisabled,
  ...numberInputProps
}: {
  field: NumericField;
  node: IRowNode<ManualFillCareOrderBlotterRow>;
  gridApi: GridApi<ManualFillCareOrderBlotterRow>;
  column: ManualFillInputColumnType;
  id?: string;
  onChange: (value: string | undefined) => void;
  onReset?: () => void;
  resetDisabled?: boolean;
  suffix?: string;
  'data-testid': string;
}) {
  const error = field.errorString;
  const warning = error ? undefined : field.warningString;
  const resetTooltip = resetDisabled
    ? 'This value is currently being derived automatically. Edit it manually to override the derived value.'
    : 'Reset to the derived value and resume automatic updates.';
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Tab') {
      return;
    }

    const handled = focusNextManualFillInput({
      currentInput: event.currentTarget,
      direction: event.shiftKey ? 'backward' : 'forward',
      gridApi,
      node,
      column,
    });
    if (!handled) {
      return;
    }

    event.preventDefault();
  };

  return (
    <NumberInput
      value={field.displayValue}
      disabled={field.isDisabled}
      invalid={!!error}
      onKeyDown={handleKeyDown}
      prefix={
        onReset || error || warning ? (
          <HStack gap="spacing4">
            {onReset && !field.isDisabled ? (
              <Tooltip tooltip={resetTooltip}>
                <IconButton
                  size={FormControlSizes.Small}
                  onClick={onReset}
                  icon={IconName.Refresh}
                  disabled={resetDisabled}
                  data-testid={`${numberInputProps['data-testid']}-reset-button`}
                  tabIndex={-1}
                />
              </Tooltip>
            ) : null}
            {error || warning ? (
              <Tooltip tooltip={error || warning}>
                <Icon
                  icon={IconName.ExclamationSolid}
                  color={error ? 'colorTextNegative' : 'colorTextWarning'}
                  data-testid={`${numberInputProps['data-testid']}-status-icon`}
                />
              </Tooltip>
            ) : null}
          </HStack>
        ) : undefined
      }
      {...numberInputProps}
    />
  );
}
